/* HUENIT LAB 2.0 — Admin: 그림·툴패스 예제 관리 (드로잉/레이저 탭용 공식 예제).
   앱 요청서 BE-0007 계약 발행 — extras.domain='engrave' + payload(picture|project) +
   kind(drawing/laser) + source_ext. manifest 는 official/manifest.engrave.json 으로
   분리(구버전 앱 회귀 방지 — repo.js 상단 주석 참고).
   카테고리·필터 태그·다국어·썸네일·배치 순서·페이지네이션은 코드 예제와 동일 인프라 재사용. */
import { useState, useRef, useEffect, useCallback } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, Confirm, MLText, Seg, useToast, Loading, ErrorState, EmptyState } from '../../components/index.js';
import { useLocal, LANG_SEED, SEED_ENGRAVE_CATS, SEED_ENGRAVE_FILTERS } from '../../data/store.js';
import { mlGet } from '../../lib/ml.js';
import { useSession } from '../../auth/session.jsx';
import { newAssetId } from '../examples/repo.js';
import { ThumbDrop } from '../examples/ExamplesAdmin.jsx';
import { downloadObject } from '../../lib/download.js';
import {
  listEngraveExamples, saveEngraveExample, deleteEngraveExample,
  engraveFileUrl, objectNameFor, validateEngraveKinds,
} from './repo.js';
import { acceptFor, validateFileName, validateBuffer, isRasterExt } from './validate.js';

const KIND_LABEL = { picture: '그림', toolpath: '툴패스' };
const KIND_ICON = { picture: 'image', toolpath: 'toolpath' };
const TARGETS = [{ id: 'drawing', label: '드로잉' }, { id: 'laser', label: '레이저' }];

/* ============ 파일 드롭존 ============ */
function EngraveFileDrop({ kind, fileName, onPick, onClear }) {
  const toast = useToast();
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [checking, setChecking] = useState(false);
  const accept = acceptFor(kind);

  // 드래그&드롭은 input 의 accept 를 우회하므로 확장자를 직접 검증하고, 내용(매직바이트)까지
  // 여기서 확인한다 — 툴패스(.huenit 평문 JSON)와 코드 프로젝트(.huenit gzip)는 확장자가
  // 같아서, 저장 시점이 아니라 고르는 순간 걸러 줘야 실수를 알아챌 수 있다.
  const pick = async (f) => {
    if (!f) return;
    const nameCheck = validateFileName(kind, f.name);
    if (!nameCheck.ok) { toast(nameCheck.error, 'error'); return; }
    setChecking(true);
    try {
      const buf = await f.arrayBuffer();
      const bodyCheck = validateBuffer(kind, nameCheck.ext, buf);
      if (!bodyCheck.ok) { toast(bodyCheck.error, 'error'); return; }
      onPick(f, nameCheck.ext);
    } catch (e) {
      toast('파일을 읽지 못했습니다: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setChecking(false);
    }
  };

  const hint = kind === 'toolpath'
    ? '드로잉·레이저 탭에서 저장한 프로젝트(.huenit)'
    : `그림 원본 (${accept.map(a => a.slice(1)).join(' · ')})`;

  return (
    <div>
      {fileName ? (
        <div className="dropzone__file">
          <Icon name={KIND_ICON[kind]} size={18} />
          <span className="dropzone__file-name">{fileName}</span>
          <button type="button" className="icon-btn icon-btn--ghost" onClick={onClear}><Icon name="close" size={16} /></button>
        </div>
      ) : (
        <div className={"dropzone" + (over ? " is-over" : "")}
          onClick={() => { if (!checking && inputRef.current) inputRef.current.click(); }}
          onDragOver={e => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={e => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}>
          <div className="dropzone__icon"><Icon name="upload" size={22} /></div>
          <div className="dropzone__t">{checking ? '파일 확인 중…' : hint + '을 끌어다 놓기'}</div>
          <div className="dropzone__d">또는 클릭해서 선택 · 앱에서 저장/내보낸 파일</div>
          <input ref={inputRef} type="file" accept={accept.join(',')} hidden onChange={e => pick(e.target.files[0])} />
        </div>
      )}
    </div>
  );
}

/* ============ 사용 대상(드로잉·레이저) ============ */
function TargetPicker({ kind, value, onChange }) {
  const single = kind === 'toolpath';
  const toggle = (id) => {
    if (single) { onChange([id]); return; }
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  };
  return (
    <div>
      <div className="ex-card__tags" style={{ marginTop: 2 }}>
        {TARGETS.map(t => {
          const on = value.includes(t.id);
          return (
            <button key={t.id} type="button" className="chip" onClick={() => toggle(t.id)}
              style={{ cursor: 'pointer', background: on ? 'var(--brand-600)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-600)' }}>
              {t.label}
            </button>
          );
        })}
      </div>
      <div className="field__hint">
        {single
          ? '툴패스는 출력 설정(레이저 파워·속도 / 펜 Z)이 들어 있어 한쪽 전용입니다 — 하나만 선택하세요.'
          : '그림 원본은 양쪽에서 쓸 수 있습니다 — 보여줄 곳을 모두 선택하세요.'}
      </div>
    </div>
  );
}

/* ============ 추가/수정 모달 ============ */
function EngraveModal({ draft, setDraft, cats, filters, langs, onSave, onClose, isNew, saving, step }) {
  const d = draft;
  const set = (patch) => setDraft({ ...d, ...patch });
  const toggleFilter = (id) => set({ filterIds: d.filterIds.includes(id) ? d.filterIds.filter(x => x !== id) : [...d.filterIds, id] });
  const def = (langs.find(l => l.default) || langs[0]).code;
  const hasFile = isNew ? !!d.fileObj : (!!d.fileObj || !!d.file);
  const kindErr = validateEngraveKinds(d.kind, d.engraveKinds);
  const canSave = !saving && (d.title[def] || '').trim() && d.catId && hasFile && !kindErr;

  // 그림(래스터)은 올린 이미지를 그대로 썸네일 후보로 채워 준다 — 따로 만들 필요가 없다.
  const onPickFile = (f, ext) => {
    const patch = { fileObj: f, file: f.name, sourceExt: ext };
    if (d.kind === 'picture' && isRasterExt(ext) && !d.thumbnail && !d.thumbFile) {
      patch.thumbFile = f; patch.thumbRemoved = false;
    }
    set(patch);
  };

  return (
    <Modal wide title={isNew ? '예제 추가' : '예제 수정'} sub="제목·내용은 언어별로 입력할 수 있어요." onClose={saving ? () => {} : onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose} disabled={saving}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={onSave}>
          {saving ? (step || '저장 중…') : (isNew ? '예제 등록' : '저장')}
        </button>
      </>}>
      <div className="field">
        <label className="field__label">예제 종류</label>
        <Seg value={d.kind} onChange={v => { if (isNew) set({ kind: v, file: '', fileObj: null, sourceExt: '', engraveKinds: v === 'toolpath' ? ['drawing'] : ['drawing', 'laser'] }); }}
             options={[{ value: 'picture', label: '그림', icon: 'image' }, { value: 'toolpath', label: '툴패스', icon: 'toolpath' }]} />
        {!isNew && <div className="field__hint">수정 시 예제 종류는 변경할 수 없습니다.</div>}
      </div>
      <div className="field">
        <label className="field__label">예제 파일<span className="req">*</span></label>
        <EngraveFileDrop kind={d.kind} fileName={d.file} onPick={onPickFile}
          onClear={() => set({ fileObj: null, file: '', sourceExt: '' })} />
      </div>
      <div className="field">
        <label className="field__label">사용 대상<span className="req">*</span></label>
        <TargetPicker kind={d.kind} value={d.engraveKinds} onChange={v => set({ engraveKinds: v })} />
        {kindErr && <div className="field__hint" style={{ color: 'var(--danger, #d33)' }}>{kindErr}</div>}
      </div>
      <div className="field">
        <label className="field__label">썸네일 <span style={{ fontWeight: 500, color: 'var(--ink-400)' }}>(선택)</span></label>
        <ThumbDrop url={d.thumbnail} file={d.thumbFile}
          onPick={f => set({ thumbFile: f, thumbRemoved: false })}
          onRemove={() => set({ thumbFile: null, thumbnail: '', thumbRemoved: true })} />
      </div>
      <div className="field">
        <label className="field__label">제목<span className="req">*</span></label>
        <MLText value={d.title} onChange={v => set({ title: v })} langs={langs} placeholder="예제 제목" />
      </div>
      <div className="field">
        <label className="field__label">내용 설명</label>
        <MLText value={d.desc} onChange={v => set({ desc: v })} langs={langs} multiline placeholder="예제에 대한 설명" />
      </div>
      <div className="field-grid">
        <div className="field">
          <label className="field__label">카테고리<span className="req">*</span></label>
          <select className="select-el" value={d.catId} onChange={e => set({ catId: e.target.value })}>
            <option value="">선택하세요</option>
            {cats.map(c => <option key={c.id} value={c.id}>{mlGet(c.label, def)}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="field__label">필터 태그</label>
          <div className="ex-card__tags" style={{ marginTop: 2 }}>
            {filters.map(f => {
              const on = d.filterIds.includes(f.id);
              return <button key={f.id} type="button" className="chip" onClick={() => toggleFilter(f.id)}
                style={{ cursor: 'pointer', background: on ? 'var(--brand-600)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-600)' }}>{mlGet(f.label, def)}</button>;
            })}
            {filters.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--ink-400)' }}>등록된 필터가 없습니다.</span>}
          </div>
        </div>
      </div>
      <div className="field">
        <label className="field__label">배치 순서 <span style={{ fontWeight: 500, color: 'var(--ink-400)' }}>(선택 · 묶음-번호)</span></label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input className="input mono" style={{ width: 88, textAlign: 'center' }} type="number" min="1" placeholder="묶음"
            value={d.orderGroup} onChange={e => set({ orderGroup: e.target.value })} />
          <span style={{ fontWeight: 800, color: 'var(--ink-400)' }}>-</span>
          <input className="input mono" style={{ width: 88, textAlign: 'center' }} type="number" min="1" placeholder="번호"
            value={d.orderIndex} onChange={e => set({ orderIndex: e.target.value })} />
          {(d.orderGroup !== '' || d.orderIndex !== '') && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => set({ orderGroup: '', orderIndex: '' })}>지우기</button>
          )}
        </div>
        <div className="field__hint">쉬운 것부터 앞으로. 번호는 <b>{KIND_LABEL[d.kind]} 예제 안에서만</b> 밀립니다({KIND_LABEL[d.kind === 'picture' ? 'toolpath' : 'picture']}·코드 예제는 영향 없음).</div>
      </div>
    </Modal>
  );
}

/* ============ 그림·툴패스 예제 관리 ============ */
export function ExamplesEngraveAdmin() {
  const toast = useToast();
  const { mode, email, getCredentials } = useSession();
  const [langs] = useLocal('adm.langs', LANG_SEED);
  // ★ 코드 예제와 별도 축 — 분류 관리의 '그림·툴패스' 탭에서 편집하고
  //   taxonomy.json 의 engrave_cats / engrave_filters 로 게시된다.
  const [cats] = useLocal('adm.engraveCats', SEED_ENGRAVE_CATS);
  const [filters] = useLocal('adm.engraveFilters', SEED_ENGRAVE_FILTERS);

  const def = (langs.find(l => l.default) || langs[0]).code;
  const [items, setItems] = useState(null);   // null=로딩, []=빈, [..]=목록
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('picture');
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState('');
  const [del, setDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 6;

  const owner = () => ({ email: email || '', defaultLang: def, cats, filters });

  const load = useCallback(async () => {
    setError(null); setItems(null);
    try {
      setItems(await listEngraveExamples());
    } catch (e) {
      setError(e && e.message ? e.message : String(e));
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const blank = (kind) => ({
    id: newAssetId(), kind, file: '', fileObj: null, sourceExt: '',
    engraveKinds: kind === 'toolpath' ? ['drawing'] : ['drawing', 'laser'],
    catId: '', filterIds: [], title: {}, desc: {},
    thumbnail: '', thumbFile: null, thumbRemoved: false, orderGroup: '', orderIndex: '',
  });
  const openNew = () => { setDraft(blank(tab)); setEditing('new'); };
  const openEdit = (ex) => { setDraft({ ...JSON.parse(JSON.stringify(ex)), fileObj: null, thumbFile: null, thumbRemoved: false }); setEditing(ex.id); };
  const closeModal = () => { if (!saving) { setEditing(null); setDraft(null); } };

  const save = async () => {
    setSaving(true); setStep('');
    try {
      const fileBuffer = draft.fileObj ? await draft.fileObj.arrayBuffer() : null;
      await saveEngraveExample(getCredentials, {
        example: draft, fileBuffer, thumbnailFile: draft.thumbFile,
        removeThumbnail: draft.thumbRemoved, owner: owner(), onStep: setStep,
      });
      toast(editing === 'new' ? '예제를 등록했습니다' : '예제를 수정했습니다');
      setTab(draft.kind);
      setEditing(null); setDraft(null);
      await load();
    } catch (e) {
      toast('저장 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setSaving(false); setStep('');
    }
  };

  const remove = async () => {
    setDelBusy(true);
    try {
      await deleteEngraveExample(getCredentials, del);
      toast('예제를 삭제했습니다');
      setDel(null);
      await load();
    } catch (e) {
      toast('삭제 실패: ' + (e && e.message ? e.message : e), 'error');
    } finally {
      setDelBusy(false);
    }
  };

  const download = async (ex) => {
    if (mode === 'fake') { toast('데모(오프라인) 모드에서는 실제 파일 다운로드를 지원하지 않습니다.'); return; }
    try {
      await downloadObject(engraveFileUrl(ex), ex.file || objectNameFor(ex.kind, ex.sourceExt));
    } catch (e) {
      toast('다운로드 실패: ' + (e && e.message ? e.message : e), 'error');
    }
  };

  const catById = (id) => cats.find(c => c.id === id);
  const list = items || [];
  const count = (k) => list.filter(e => e.kind === k).length;
  const shown = list.filter(e =>
    e.kind === tab &&
    (catFilter === 'all' || e.catId === catFilter) &&
    (q.trim() === '' || mlGet(e.title, def).toLowerCase().includes(q.trim().toLowerCase())));
  // 6개씩 페이지네이션 (shown 은 이미 배치 순서 정렬됨).
  const totalPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  useEffect(() => { setPage(1); }, [q, catFilter, tab]);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Seg value={tab} onChange={setTab} options={[
          { value: 'picture', label: `그림 예제${items ? ` (${count('picture')})` : ''}`, icon: 'image' },
          { value: 'toolpath', label: `툴패스 예제${items ? ` (${count('toolpath')})` : ''}`, icon: 'toolpath' },
        ]} />
        <div className="field__hint" style={{ marginTop: 6 }}>
          {tab === 'picture'
            ? '이미지·SVG·DXF 원본. 드로잉·레이저 양쪽에서 불러 쓸 수 있습니다.'
            : '드로잉·레이저에서 저장한 프로젝트(.huenit). 출력 설정이 포함되어 한쪽 전용입니다.'}
        </div>
      </div>

      <div className="toolbar">
        <div className="search"><Icon name="search" size={16} /><input value={q} onChange={e => setQ(e.target.value)} placeholder="예제 제목 검색.." /></div>
        <select className="select-el" style={{ width: 'auto' }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="all">전체 카테고리</option>
          {cats.map(c => <option key={c.id} value={c.id}>{mlGet(c.label, def)}</option>)}
        </select>
        <button className="btn btn--primary" onClick={openNew}><Icon name="plus" size={15} stroke={2.2} />{KIND_LABEL[tab]} 예제 추가</button>
      </div>

      {items === null ? (
        <Loading title="불러오는 중…" sub="그림·툴패스 예제 목록을 가져오고 있습니다." />
      ) : error ? (
        <ErrorState error={error} title="목록을 불러오지 못했습니다" onRetry={load} />
      ) : shown.length === 0 ? (
        <EmptyState icon={KIND_ICON[tab]} title={`${KIND_LABEL[tab]} 예제가 없습니다`}
          sub={`우측 상단 [${KIND_LABEL[tab]} 예제 추가]로 첫 예제를 등록해보세요.`} />
      ) : (
        <div className="ex-grid">
          {shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(ex => {
            const c = catById(ex.catId);
            const hasOrder = ex.orderGroup !== '' && ex.orderIndex !== '';
            return (
              <div key={ex.id} className="ex-card">
                <div className="ex-card__top">
                  <span className={"ex-card__kind ex-card__kind--" + ex.kind}>
                    <Icon name={KIND_ICON[ex.kind]} size={13} />{KIND_LABEL[ex.kind]}
                  </span>
                  {hasOrder && <span className="chip mono" style={{ background: 'var(--brand-600)', color: '#fff', fontWeight: 800 }}>{ex.orderGroup}-{ex.orderIndex}</span>}
                  {c && <span className="ex-card__cat"><span className="dot" style={{ background: c.color }}></span>{mlGet(c.label, def)}</span>}
                </div>
                <div className="ex-card__body">
                  <div className="ex-card__title">{mlGet(ex.title, def) || '(제목 없음)'}</div>
                  {mlGet(ex.desc, def) && <div className="ex-card__desc">{mlGet(ex.desc, def)}</div>}
                  {ex.file && <div className="ex-card__file"><Icon name="file" size={14} />{ex.file}</div>}
                  <div className="ex-card__tags" style={{ marginTop: 6 }}>
                    {TARGETS.filter(t => ex.engraveKinds.includes(t.id)).map(t => (
                      <span key={t.id} className="chip" style={{ background: '#eef5ff', color: '#2e6fff' }}>{t.label}</span>
                    ))}
                  </div>
                  {ex.filterIds.length > 0 && (
                    <div className="ex-card__tags">
                      {ex.filterIds.map(fid => { const f = filters.find(x => x.id === fid); return f ? <span key={fid} className="chip">{mlGet(f.label, def)}</span> : null; })}
                    </div>
                  )}
                  {ex.thumbnail && <img src={ex.thumbnail} alt="" onError={e => { e.currentTarget.style.display = 'none'; }}
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 10, marginTop: 12 }} />}
                </div>
                <div className="ex-card__foot">
                  <div className="ex-card__langs">
                    {langs.map(l => <span key={l.code} className={"lang-pip" + ((ex.title[l.code] || '').trim() ? " is-set" : "")}>{l.code}</span>)}
                  </div>
                  <button className="icon-btn" onClick={() => download(ex)} title="파일 다운로드"><Icon name="download" size={15} /></button>
                  <button className="icon-btn" onClick={() => openEdit(ex)} title="수정"><Icon name="file" size={15} /></button>
                  <button className="icon-btn icon-btn--danger" onClick={() => setDel(ex)} title="삭제"><Icon name="trash" size={15} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {items !== null && !error && shown.length > PAGE_SIZE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 22 }}>
          <button className="btn btn--ghost btn--sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><Icon name="chev-l" size={14} />이전</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
            <button key={n} className={"btn btn--sm" + (n === page ? " btn--primary" : " btn--ghost")} style={{ minWidth: 34 }} onClick={() => setPage(n)}>{n}</button>
          ))}
          <button className="btn btn--ghost btn--sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>다음<Icon name="chev-r" size={14} /></button>
        </div>
      )}

      {editing && draft && (
        <EngraveModal draft={draft} setDraft={setDraft} cats={cats} filters={filters} langs={langs}
          isNew={editing === 'new'} saving={saving} step={step}
          onSave={save} onClose={closeModal} />
      )}
      {del && <Confirm title="예제를 삭제할까요?"
        body={<><b>{mlGet(del.title, def)}</b> 예제를 삭제합니다. 되돌릴 수 없습니다.</>}
        confirmLabel={delBusy ? '삭제 중…' : '삭제'}
        onConfirm={delBusy ? () => {} : remove} onClose={delBusy ? () => {} : () => setDel(null)} />}
    </div>
  );
}

export default ExamplesEngraveAdmin;
